import { useMemo } from 'react';
import { usePhoneNumbers } from '@/lib/messaging/hooks';

/**
 * Returns a stable Set of E.164 phone numbers that belong to the current
 * tenant. Used by the inbox to flag "intra-account" conversations — when a
 * platform-admin sends a test SMS from one of our own Telnyx numbers to
 * another (typically to verify the inbound webhook), the resulting
 * conversation's contact phone matches one of our own lines. Without a
 * visual cue the operator would mistake it for a real customer.
 *
 * Pulls from the same `ACTIVE` filter the inbox composer already uses, so
 * we don't fire a duplicate request. Returns an empty Set while the
 * underlying query is loading — callers should treat the result as a
 * monotonically-growing source of truth.
 */
export function useTenantPhoneSet(): Set<string> {
  const phoneNumbersQuery = usePhoneNumbers({ status: 'ACTIVE' });

  return useMemo(() => {
    const set = new Set<string>();
    for (const n of phoneNumbersQuery.data?.data ?? []) {
      if (n.e164) set.add(n.e164);
    }
    return set;
  }, [phoneNumbersQuery.data]);
}
